import {useAsyncFn, useAsyncRetry, useList} from 'react-use';
import {getStatusColor} from './logs-listing';
import {LogHandleStatuses} from '../api/generated/enums';
import {LogGroupNotesService} from '../api/generated/LogGroupNotesService';
import {LogSummariesService} from '../api/generated/LogSummariesService';
import {useProduce} from '../hooks/use-produce';
import {
  LogSummaryDetailDto,
  LogSummaryListDto,
} from '../api/generated/index.defs';
import {
  Badge,
  Button,
  Divider,
  FloatingIndicator,
  Grid,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {ROUTES} from '../routes';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {CodeHighlight} from '@mantine/code-highlight';
import {useCallback, useState} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {z} from 'zod';
import {createForm} from '../forms';
import {css} from '@emotion/react';
import {BasicPage} from '../components/basic-page.tsx';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {DateTime} from 'luxon';
import {formatJSDate} from '../utils/luxon.ts';
import {ConfirmButton} from '../components/confirm-button.tsx';
import {faCheck, faTimes} from '@fortawesome/pro-regular-svg-icons';
import {notifications} from '../utils/notification-service.ts';
import {CopyButton} from '../components/copy-button.tsx';
import {DateFormat} from '../components/date.tsx';
import classes from './log-group-detail.module.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {mapNullToUndefined} from '../utils/zod.ts';

export type LogGroupParams = {
  statusCode: number | undefined;
  errorMessages: string | undefined;
  matchedPattern: string | undefined;
};

type LogGroupDetailState = {
  currentPage: number;
  selectedLogId: number;
  openNotes: boolean;
};

const DefaultState: LogGroupDetailState = {
  currentPage: 1,
  selectedLogId: 0,
  openNotes: false,
};

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Logs', href: ROUTES.ADMIN.LOGS.INDEX.path},
  {title: 'Log Details'},
];

const NotesSchema = z.object({
  notes: mapNullToUndefined(z.string().optional()),
});
type NotesSchema = z.infer<typeof NotesSchema>;

const Form = createForm<NotesSchema>();

export const LogGroupDetail = () => {
  const [list, {clear, upsert}] = useList<LogSummaryListDto>();
  const [state, setState] = useProduce<LogGroupDetailState>(DefaultState);

  const {parameters} = useTypedParams(ROUTES.ADMIN.LOGS.GROUPED_DETAIL);

  const fetchLogs = useAsyncRetry(async () => {
    const response = await LogSummariesService.getByGroup({
      parameters,
      pageSize: list.length !== 0 ? 7 : 7 * state.currentPage,
      page: list.length !== 0 ? state.currentPage : 1,
    });
    if (response.hasErrors) {
      notifications.errors(response.errors);
    } else {
      if (response.data) {
        response.data.items.forEach((item) =>
          upsert((a, b) => a.id === b.id, item),
        );
        !state.selectedLogId &&
          setState(
            (draft) =>
              (draft.selectedLogId = Number(response.data?.items[0].id)),
          );
      }

      return response.data;
    }
  });

  const fetchSelectedLog = useAsyncRetry(async () => {
    if (!state.selectedLogId) return;

    const {data} = await LogSummariesService.getById({
      id: state.selectedLogId,
    });

    return data;
  }, [state.selectedLogId]);

  const logGroupParams = JSON.parse(atob(parameters)) as LogGroupParams;
  const logs = fetchLogs.value;
  const selectedLog = fetchSelectedLog.value;

  const fetchNotes = useAsyncRetry(async () => {
    const {data} = await LogGroupNotesService.getByParameters({parameters});
    return data;
  });

  const notes = fetchNotes.value;

  const [resolveAllState, resolveAll] = useAsyncFn(async () => {
    const response = await LogSummariesService.resolveGroup({parameters});

    if (response.hasErrors) {
      return response;
    }

    notifications.success('Resolved all occurrences');
  });

  const [updateHandleStatusState, updateHandleStatus] = useAsyncFn(
    async (log: LogSummaryDetailDto) => {
      const status =
        log.handledStatus === 'Resolved' ? 'Unresolved' : 'Resolved';

      const response = await LogSummariesService.updateHandledStatus({
        id: Number(log.id),
        status,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success(`Occurrence marked as ${status.toLowerCase()}`);
    },
  );

  const onSubmit = useCallback<SubmitHandler<any>>(
    async (values) => {
      const response = await LogGroupNotesService.update({
        notes: values.notes,
        parameters,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Notes updated');
      setState((draft) => (draft.openNotes = false));
      fetchNotes.retry();
    },
    [fetchNotes, parameters, setState],
  );

  const handleResolveAll = async () => {
    await resolveAll();
    refreshList();
  };

  const handleUpdateHandleStatus = async (log: LogSummaryDetailDto) => {
    await updateHandleStatus(log);
    refreshList();
  };

  const refreshList = () => {
    clear();
    fetchLogs.retry();
    fetchSelectedLog.retry();
  };

  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [controlsRefs, setControlsRefs] = useState<
    Record<string, HTMLButtonElement | null>
  >({});

  const setControlRef = (index: number) => (node: HTMLButtonElement) => {
    controlsRefs[index] = node;
    setControlsRefs(controlsRefs);
  };

  return (
    <BasicPage title={breadcrumbs} css={styles}>
      {list && logs && (
        <Stack>
          <Paper p={'md'} component={Stack}>
            <Grid align={'center'}>
              <Grid.Col span={'auto'}>
                <Title
                  c={getStatusColor(logGroupParams.statusCode)}
                  className='status'
                >
                  {logGroupParams.statusCode}
                </Title>
                <Title size='huge' className='occurrences'>
                  {logs.totalItemCount}{' '}
                  {logs.totalItemCount > 1 ? 'occurrences' : 'occurrence'}
                </Title>
                {list[0]?.timestamp && (
                  <time
                    title={formatJSDate(
                      list[0]?.timestamp,
                      DateTime.DATETIME_FULL,
                    )}
                  >
                    Last occurred{' '}
                    {DateTime.fromISO(String(list[0]?.timestamp)).toRelative()}
                  </time>
                )}
              </Grid.Col>
              <Grid.Col span={'auto'}>
                <Title order={2}>{logGroupParams.matchedPattern}</Title>
                {logGroupParams.errorMessages}
              </Grid.Col>
              <Grid.Col span={'auto'}>
                <Group justify={'flex-end'}>
                  <Button
                    variant='outline'
                    color={'blue'}
                    onClick={() =>
                      setState((draft) => (draft.openNotes = true))
                    }
                  >
                    View Notes
                  </Button>
                  <ConfirmButton
                    onConfirm={handleResolveAll}
                    loading={resolveAllState.loading}
                    text='Resolve All'
                    loadingText='Resolving all...'
                    icon={faCheck}
                    variant='subtle'
                    color={'blue'}
                  />
                </Group>
              </Grid.Col>
            </Grid>
          </Paper>
          <Grid className='table-container' gutter={'xl'}>
            <Grid.Col span={9}>
              {selectedLog && (
                <Paper px={'md'} pb={'md'}>
                  <Group pt={'sm'} justify={'space-between'}>
                    {selectedLog.handledStatus && (
                      <Badge
                        color={getResolvedStatusColor(
                          selectedLog.handledStatus,
                        )}
                      >
                        {selectedLog.handledStatus}
                      </Badge>
                    )}
                    {isResolved(selectedLog.handledStatus) ? (
                      <ConfirmButton
                        onConfirm={() => handleUpdateHandleStatus(selectedLog)}
                        loading={updateHandleStatusState.loading}
                        text='Mark as Unresolved'
                        loadingText='Unresolving...'
                        icon={faTimes}
                        color={'gray.4'}
                        variant='subtle'
                      />
                    ) : (
                      <ConfirmButton
                        onConfirm={() => handleUpdateHandleStatus(selectedLog)}
                        loading={updateHandleStatusState.loading}
                        text='Mark as Resolved'
                        loadingText='Resolving...'
                        icon={faCheck}
                        color={'green.5'}
                        variant='subtle'
                      />
                    )}
                  </Group>
                  <Tabs defaultValue={'Details'}>
                    <Tabs.List>
                      <Tabs.Tab value='Details'>Details</Tabs.Tab>
                      <Tabs.Tab value='Stack'>Stack</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value='Details'>
                      <Table>
                        <Table.Tbody>
                          <Table.Tr>
                            <Table.Td>Timestamp</Table.Td>
                            <Table.Td>
                              {selectedLog.timestamp && (
                                <Group>
                                  <Tooltip
                                    label={formatJSDate(
                                      selectedLog.timestamp,
                                      DateTime.DATETIME_FULL,
                                    )}
                                  >
                                    <Text>{String(selectedLog.timestamp)}</Text>
                                  </Tooltip>
                                  <CopyButton
                                    value={String(selectedLog.timestamp)}
                                  />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td>Route</Table.Td>
                            <Table.Td>
                              {selectedLog.route && (
                                <Group>
                                  <Text>{selectedLog.route}</Text>
                                  <CopyButton value={selectedLog.route} />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td>Duration</Table.Td>
                            <Table.Td>
                              {selectedLog.duration && (
                                <Group>
                                  <Text>{selectedLog.duration}</Text>
                                  <CopyButton value={selectedLog.duration} />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td>User ID</Table.Td>
                            <Table.Td>
                              {selectedLog.userId && (
                                <Group>
                                  <Text>{selectedLog.userId}</Text>
                                  <CopyButton value={selectedLog.userId} />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td>IP Address</Table.Td>
                            <Table.Td>
                              {selectedLog.ipAddress && (
                                <Group>
                                  <Text>{selectedLog.ipAddress}</Text>
                                  <CopyButton value={selectedLog.ipAddress} />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td>Trace</Table.Td>
                            <Table.Td>
                              {selectedLog.trace && (
                                <Group>
                                  <Text>{selectedLog.trace}</Text>
                                  <CopyButton value={selectedLog.trace} />
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        </Table.Tbody>
                      </Table>
                    </Tabs.Panel>
                    <Tabs.Panel value='Stack'>
                      <CodeHighlight
                        code={selectedLog.stack ?? ''}
                        language={'accessLog'}
                      />
                    </Tabs.Panel>
                  </Tabs>
                </Paper>
              )}
            </Grid.Col>
            <Grid.Col span={3}>
              <ScrollArea h={550}>
                {list.length > 1 && (
                  <Paper
                    p={'xs'}
                    ref={setRootRef}
                    className={classes.root}
                    component={Stack}
                    gap={'xs'}
                  >
                    {list.map((item) => (
                      <UnstyledButton
                        className={classes.control}
                        ref={setControlRef(item.id!)}
                        onClick={() =>
                          setState((draft) => (draft.selectedLogId = item.id!))
                        }
                        key={item.id}
                        mod={{active: state.selectedLogId === item.id}}
                      >
                        <Group className={classes.controlLabel}>
                          <Stack gap={0} w={'100%'}>
                            <Group wrap={'nowrap'} justify={'space-between'}>
                              <DateFormat
                                date={item.timestamp!}
                                format={DateTime.DATETIME_FULL}
                                fz={14}
                                fw={500}
                              />
                              <Badge
                                variant='light'
                                color={
                                  isResolved(item.handledStatus)
                                    ? 'green'
                                    : 'red'
                                }
                              >
                                <FontAwesomeIcon
                                  icon={
                                    isResolved(item.handledStatus)
                                      ? faCheck
                                      : faTimes
                                  }
                                />
                              </Badge>
                            </Group>
                            <Text size='sm' c='dimmed'>
                              {item.duration}
                            </Text>
                            <Text size='sm' c='dimmed'>
                              {item.route}
                            </Text>
                          </Stack>
                        </Group>
                      </UnstyledButton>
                    ))}
                    <FloatingIndicator
                      target={controlsRefs[state.selectedLogId]}
                      parent={rootRef}
                      className={classes.indicator}
                    />
                    {list.length < logs.totalItemCount && (
                      <>
                        <Divider />
                        <Button
                          loading={fetchLogs.loading}
                          onClick={() => {
                            setState((draft) => draft.currentPage++);
                            fetchLogs.retry();
                          }}
                        >
                          Load More
                        </Button>
                      </>
                    )}
                  </Paper>
                )}
              </ScrollArea>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
      <Modal
        opened={state.openNotes}
        onClose={() => setState((draft) => (draft.openNotes = false))}
        lockScroll={false}
        title={<Title order={4}>Log Notes</Title>}
      >
        <Form
          defaultValues={notes || {}}
          onSubmit={onSubmit}
          schema={NotesSchema}
        >
          <Form.TextArea name='notes' label='Notes' rows={12} />
          <Form.Actions>
            <Button type='submit'>Save</Button>
          </Form.Actions>
        </Form>
      </Modal>
    </BasicPage>
  );
};

const getResolvedStatusColor = (status: string | undefined) => {
  const typedStatus = status as LogHandleStatuses;
  switch (typedStatus) {
    case 'Resolved':
      return 'green';
    case 'Unresolved':
      return 'red';
    default:
      return 'blue';
  }
};

const isResolved = (status: string | undefined) => {
  const typedStatus = status as LogHandleStatuses;
  return typedStatus === 'Resolved';
};

const styles = css`
  .table-container {
    margin-top: 1rem;
  }

  .status {
    font-size: 65px !important;
    margin-bottom: 0;
  }

  .occurrences {
    margin-top: 0;
  }
`;
